import type { ReactNode } from 'react'
import { match } from '@st/pattern'
import type { Schema } from './model'

export const inputClass =
  'w-full min-w-0 border-0 bg-transparent p-0 text-lg font-normal text-gray-900 outline-none focus:ring-0 disabled:text-gray-400'
export type InputProps = {
  schema: Schema
  value: unknown
  label: string
  onChange: (value: unknown) => void
}
export type Control = {
  // Returns an element of a module-scope input component. A control built per
  // render must not define the component itself, or React remounts the input.
  render: (props: InputProps) => ReactNode
  // An atomic type may supply a human-readable identity for a containing item.
  // Formatting a money value alone does not make it an item's identity.
  itemLabel?: (value: unknown) => string | undefined
  complete?: (value: unknown) => boolean
}
export type Controls = Record<string, Control>

function TextInput({ value, label, onChange }: InputProps) {
  return (
    <input
      className={inputClass}
      aria-label={label}
      value={String(value ?? '')}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}
// Money stays the typed string, including blank. Consumers parse it as a
// decimal string; null is not a value the schema's money scalar accepts.
function MoneyInput({ value, label, onChange }: InputProps) {
  return (
    <span className="flex min-w-0 items-baseline gap-1 text-lg font-normal text-gray-900">
      <span className="text-gray-600">$</span>
      <input
        className={inputClass}
        aria-label={label}
        inputMode="decimal"
        value={String(value ?? '')}
        onChange={(event) => onChange(event.target.value)}
      />
    </span>
  )
}
function NumberInput({ schema, value, label, onChange }: InputProps) {
  return (
    <input
      className={inputClass}
      aria-label={label}
      type="number"
      min={schema.minimum}
      max={schema.maximum}
      value={String(value ?? '')}
      onChange={(event) =>
        onChange(
          match(event.target.value)
            .with('', () => undefined)
            .otherwise(Number)
        )
      }
    />
  )
}
function BooleanInput({ value, label, onChange }: InputProps) {
  return (
    <input
      aria-label={label}
      type="checkbox"
      className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
      checked={value === true}
      onChange={(event) => onChange(event.target.checked)}
    />
  )
}
function DateInput({ value, label, onChange }: InputProps) {
  const text = String(value ?? '')
  // Keep malformed source/correction values visible. Native date inputs erase
  // invalid values, including date-shaped strings such as 2026-02-31.
  const parsed = new Date(`${text}T00:00:00Z`)
  const valid =
    /^\d{4}-\d{2}-\d{2}$/.test(text) &&
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) == text
  const type = match(text == '' || valid)
    .with(true, () => 'date')
    .otherwise(() => 'text')
  return (
    <input
      className={inputClass}
      aria-label={label}
      type={type}
      placeholder="YYYY-MM-DD"
      value={text}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}
function EnumInput({ schema, value, label, onChange }: InputProps) {
  const options = schema.enum!
  return (
    <select
      aria-label={label}
      className={inputClass}
      value={options.findIndex((option) => option === value)}
      onChange={(event) => onChange(options[Number(event.target.value)])}
    >
      <option value={-1} disabled>
        Select {label.toLowerCase()}
      </option>
      {options.map((option, index) => (
        <option key={index} value={index}>
          {String(option)}
        </option>
      ))}
    </select>
  )
}

export const standardControls: Controls = {
  string: { render: (props) => <TextInput {...props} /> },
  number: { render: (props) => <NumberInput {...props} /> },
  integer: { render: (props) => <NumberInput {...props} /> },
  boolean: { render: (props) => <BooleanInput {...props} /> },
  money: { render: (props) => <MoneyInput {...props} /> },
  date: { render: (props) => <DateInput {...props} /> },
  enum: { render: (props) => <EnumInput {...props} /> }
}
